import { Component, Host, h, Prop, Element, Watch } from "@stencil/core";
import { Customer, CustomerState, isCustomer, TLanguage } from "../../types";
import { defaultOceRequestHeaders, fetchWith } from "../../services/fetch";

function showOrHide(show: boolean, el: HTMLElement | null) {
  if (!el) return;

  el.style.setProperty("display", show ? "block" : "none");
  if (el.parentElement?.classList.contains("aem-GridColumn")) {
    el.parentElement.style.setProperty("display", show ? "block" : "none");
  }
  if (show) el.style.setProperty("height", "100%");
}

@Component({
  tag: "scs-header-coordinator",
  styleUrl: "header-coordinator.scss",
  shadow: false,
})
export class HeaderCoordinator {
  @Element()
  private host!: HTMLElement;

  /**
   * The language to use for i18n strings.
   */
  @Prop()
  public language = (document.documentElement.lang || "de") as TLanguage;

  /**
   * The logged-in customer (if any). Can be set as JSON.
   *
   * Three cases exist.
   *
   * 1. RES Customers. These only have a name, no company.
   * 2. SME Businesses. These have a company name only
   * 3. SME Businesses’ employees. These have got both properties.
   *
   * The name is expected to be normalized already:
   *
   * • The name and companyName fields must be distinct. If not, the name should be omitted.
   * • There is no distinction between first, middle and last name. Set the full name in the user's preferred format.
   */
  @Prop()
  public customer: CustomerState = "LOADING";

  private userInfo?: HTMLScsUserInfoElement;
  private search?: HTMLScsSearchInputElement;
  private basket?: HTMLScsBasketElement;
  private notifications?: HTMLScsNotificationsElement;

  public async componentWillLoad() {
    const header = this.host.closest("sdx-header")!;
    this.userInfo = header.querySelector("scs-user-info") ?? undefined;
    this.search = header.querySelector("scs-search-input") ?? undefined;
    this.basket = header.querySelector("scs-basket") ?? undefined;
    this.notifications = header.querySelector("scs-notifications") ?? undefined;
    this.updateLanguage(this.language);
    this.updateCustomer(this.customer);
  }

  @Watch("customer")
  public updateCustomer(newValue: CustomerState) {
    const customer =
      typeof newValue === "string" &&
      !["LOADING", "ANONYMOUS"].includes(newValue)
        ? (JSON.parse(newValue) as CustomerState)
        : (newValue ?? "LOADING");
    if (this.userInfo) {
      this.userInfo.customer = customer;
    }
    if (this.notifications) {
      this.notifications.customer = customer;
    }
    if (isCustomer(customer) && customer.priorityStatus === undefined) {
      this.updatePriorityStatus(customer);
    }
    if (customer === "ANONYMOUS") {
      showOrHide(true, document.querySelector("#hide-if-priority"));
    }
  }

  private async updatePriorityStatus(customer: Customer) {
    const response = await fetchWith(
      "/oce/soe/customers/v1",
      { scn: customer.scn },
      {
        headers: defaultOceRequestHeaders(
          "update-priority-status",
          this.language,
          customer.segment,
        ),
      },
    );
    customer.priorityStatus = /Platin|VIP/i.test(response.body.subSegment);
    if (this.userInfo) {
      this.userInfo.customer = structuredClone(customer);
    }
    showOrHide(
      customer.priorityStatus,
      document.querySelector("#show-if-priority"),
    );
    showOrHide(
      !customer.priorityStatus,
      document.querySelector("#hide-if-priority"),
    );
  }

  @Watch("language")
  public updateLanguage(newValue: TLanguage) {
    if (this.userInfo) {
      this.userInfo.language = newValue;
    }
    if (this.search) {
      this.search.language = newValue;
    }
    if (this.basket) {
      this.basket.language = newValue;
    }
    if (this.notifications) {
      this.notifications.language = newValue;
    }
  }

  render() {
    return <Host></Host>;
  }
}
